import React, { useEffect, useState, forwardRef } from 'react';
import { useHistory } from 'react-router-dom';

import FormSection from '../../ui/form-elements/form-page/form-section';
import InputWrapper from '../../ui/form-elements/form-page/input-wrapper';
import TextInput from '../../ui/form-elements/text-input/text-input';
import TextArea from '../../ui/form-elements/text-area/text-area';
import NumberInput from '../../ui/form-elements/text-input/number-input';
import DropdownSelect from '../../ui/dropdown';
import DateRangePicker from '../../ui/form-elements/date-picker/date-picker';
import Slider from '../../ui/slider';
import Button from '../../ui/button';

import {
  dateRange,
  max,
  required,
  maxLength,
  regexMatch,
  conditionalMax,
} from '../../ui/form-elements/validators';
import { useForm } from '../../../utils/custom-hooks';

import {
  STRATEGY_BUDGET_TYPE_LABELS,
  STRATEGY_PAYMENT_MODEL_LABELS,
  STRATEGY_PRICING_MODEL_LABELS,
} from '../../../utils/constants';

const pricingModelItems = [
  { label: 'ROI', value: 'EPP' },
  { label: 'CPC', value: 'CPC' },
];

const SetUpPage = props => {
  const {
    strategy,
    strategyId,
    strategyGroup,
    strategyGroupId,
    merchantId,
    merchantPricingModel,
    strategyDefaultValues,
    setIsDirty,
    onSubmit,
    formError,
    handleBackButton,
  } = props;
  const history = useHistory();

  // const [feeType, setFeeType] = useState(null);
  const [budgetType, setBudgetType] = useState();
  const [pricingModel, setPricingModel] = useState({});
  const [paymentModel, setPaymentModel] = useState({});
  const [pubPaymentModelToggle, setPubPaymentModelToggle] = useState(false);

  // Matches strings of format utm_param=param1&utm_otherparam=param2
  const utmRegex = /^([\w]+=[\w%+-]*)(&([\w]+=[\w%+-]+))*$/g;

  //  Input formatting
  const formatValues = strategy => {
    const strategyValues = { ...strategy };

    if (strategy.strategy_pricing_model) {
      strategyValues.strategy_pricing_model_value =
        strategy.strategy_pricing_model === 'EPP'
          ? strategy.strategy_roi_goal
          : strategy.strategy_bid;
      setPricingModel({ label: STRATEGY_PRICING_MODEL_LABELS[strategy.strategy_pricing_model] });
    }

    if (strategy.strategy_payment_model) {
      strategyValues.strategy_payment_model_value =
        strategy.strategy_payment_model === 'EPP'
          ? strategy.strategy_publisher_roi_target
          : strategy.strategy_publisher_epc;
    } else if (strategy.strategy_cogs) {
      strategyValues.strategy_payment_model = 'COGS';
      strategyValues.strategy_payment_model_value = strategy.strategy_cogs;
    }

    setPaymentModel({
      label: STRATEGY_PAYMENT_MODEL_LABELS[strategyValues.strategy_payment_model],
    });

    setBudgetType({ label: STRATEGY_BUDGET_TYPE_LABELS[strategy.strategy_budget_type] });

    const shouldTogglePayment =
      strategy.strategy_publisher_roi_target !== null ||
      strategy.strategy_publisher_epc !== null ||
      strategy.strategy_cogs !== null;

    if (shouldTogglePayment) {
      setPubPaymentModelToggle(true);
    }

    if (strategy) return strategyValues;
  };

  //  Output formatting
  const formatPayload = payload => {
    const formattedPayload = { ...payload };

    const pricingModelValue = parseFloat(formattedPayload.strategy_pricing_model_value);
    if (formattedPayload.strategy_pricing_model === 'EPP') {
      formattedPayload.strategy_roi_goal = pricingModelValue;
      formattedPayload.strategy_bid = null;
    } else if (formattedPayload.strategy_pricing_model === 'CPC') {
      formattedPayload.strategy_bid = pricingModelValue;
      formattedPayload.strategy_roi_goal = null;
    }

    if (pubPaymentModelToggle) {
      const paymentModelValue = parseFloat(formattedPayload.strategy_payment_model_value);
      if (formattedPayload.strategy_payment_model === 'EPP') {
        formattedPayload.strategy_publisher_roi_target = paymentModelValue;
        formattedPayload.strategy_publisher_epc = null;
        formattedPayload.strategy_cogs = null;
      } else if (formattedPayload.strategy_payment_model === 'EPC') {
        formattedPayload.strategy_publisher_epc = paymentModelValue;
        formattedPayload.strategy_publisher_roi_target = null;
        formattedPayload.strategy_cogs = null;
      } else {
        formattedPayload.strategy_payment_model = null;
        formattedPayload.strategy_cogs = paymentModelValue;
        formattedPayload.strategy_publisher_epc = null;
        formattedPayload.strategy_publisher_roi_target = null;
      }
    } else {
      formattedPayload.payment_model = null;
      formattedPayload.strategy_cogs = null;
      formattedPayload.strategy_publisher_epc = null;
      formattedPayload.strategy_publisher_roi_target = null;
    }

    formattedPayload.strategy_budget_amount = parseFloat(formattedPayload.strategy_budget_amount);

    //  Prevents sending empty string
    if (
      !formattedPayload.strategy_utm_query_string ||
      formattedPayload.strategy_utm_query_string.trim().length === 0
    ) {
      formattedPayload.strategy_utm_query_string = null;
    }

    if (
      !formattedPayload.strategy_description ||
      formattedPayload.strategy_description.trim().length === 0
    ) {
      formattedPayload.strategy_description = null;
    }

    Object.keys(formattedPayload).forEach(field => {
      if (!Object.keys(strategyDefaultValues).includes(field)) {
        delete formattedPayload[field];
      }
    });

    //  Non form values
    formattedPayload.strategy_priority = 'normal';

    if (!strategyId) {
      formattedPayload.merch_id = parseInt(merchantId);
      formattedPayload.strategy_group_id = parseInt(strategyGroupId);
      formattedPayload.strategy_is_enabled = true;
    }
    //

    return formattedPayload;
  };

  const validators = {
    strategy_name: [required()],
    strategy_budget_type: [required()],
    strategy_budget_amount: [max(9999999.99)],
    strategy_datetime_start: [required(), dateRange('strategy_datetime_end')],
    strategy_pricing_model_value: [required('strategy_pricing_model'), max(1000)],
    strategy_payment_model_value: [required('strategy_payment_model'), max(1000)],
    strategy_utm_query_string: [maxLength(512), regexMatch(utmRegex)],
  };

  const { values, setValues, errors, handleChange, handleSubmit, isDirty } = useForm(
    values => onSubmit(formatPayload(values)),
    validators,
  );

  const onToggle = () => {
    //  Required because validators still run on hidden/disabled inputs, values wont be stored when toggling back on
    //  TODO: Build way to disable validators conditionally
    if (pubPaymentModelToggle) {
      const newValues = { ...values };
      newValues.strategy_payment_model = null;
      newValues.strategy_payment_model_value = null;
      setValues(newValues);
    }
    setIsDirty(true);
    setPubPaymentModelToggle(!pubPaymentModelToggle);
  };

  useEffect(() => {
    let newValues = formatValues(strategy);
    if (formError) {
      newValues = Object.assign(newValues, values);
    }

    setValues(newValues);
  }, [strategy, formError]);

  //  Allows Strategy Creation Page to be in control of Discard Changes modal
  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  return (
    <form onSubmit={handleSubmit}>
      <FormSection label="Campaign Name">
        <InputWrapper name="strategy_name" label="Name" required={true}>
          <TextInput
            name="strategy_name"
            value={values.strategy_name || ''}
            onChange={handleChange}
            errors={errors.strategy_name}
          />
        </InputWrapper>
        <InputWrapper name="strategy_description" label="Description">
          <TextArea
            name="strategy_description"
            value={values.strategy_description || ''}
            onChange={handleChange}
          />
        </InputWrapper>

        {/* <InputWrapper name="strategy_fee_type" label="Campaign Type" required={true}> */}
        {/* <DropdownSelect */}
        {/*   selected={[values.strategy_fee_type] || null} */}
        {/*   onChange={handleChange} */}
        {/*   onSelect={setFeeType} */}
        {/*   styleType="bordered" */}
        {/*   formElement={true} */}
        {/*   name="strategy_fee_type" */}
        {/*   label={feeType ? feeType.label : 'Select Type'} */}
        {/*   required={true} */}
        {/*   menuAlign="left" */}
        {/*   items={[ */}

        {/*   ]} */}
        {/* /> */}
        {/* </InputWrapper> */}
      </FormSection>
      <FormSection
        label="Campaign Details"
        description={
          'Budget "Cap" is the max amount of money that can be spent for this strategy, which draw from the overarching campaign budget.'
        }
      >
        <InputWrapper label="Budget Cap" width={50}>
          <NumberInput
            name="strategy_budget_amount"
            value={values.strategy_budget_amount || ''}
            onChange={handleChange}
            subLabel={'US Dollars'}
            errors={errors.strategy_budget_amount}
          />
          <DropdownSelect
            onChange={handleChange}
            onSelect={setBudgetType}
            styleType="bordered"
            formElement={true}
            label={budgetType ? budgetType.label : 'Select Interval'}
            name="strategy_budget_type"
            errors={errors.strategy_budget_type}
            items={[
              { label: 'Lifetime', value: 'lifetime' },
              { label: 'Monthly', value: 'monthly' },
            ]}
          />
        </InputWrapper>
        <InputWrapper label="Date" required={true} width={50}>
          <DateRangePicker
            datetimeStart={values.strategy_datetime_start || null}
            datetimeEnd={values.strategy_datetime_end || null}
            onChange={handleChange}
            startDateField="strategy_datetime_start"
            endDateField="strategy_datetime_end"
            endDateOptional={true}
            errors={{
              strategy_datetime_start: errors.strategy_datetime_start,
              strategy_datetime_end: errors.strategy_datetime_end,
            }}
            rangeLimit={strategyGroup ? [strategyGroup.strategy_group_datetime_start, null] : null}
            //  Awaiting confirmation from Product
            // disableStartDate={strategyId}
          />
        </InputWrapper>
        <InputWrapper label="Pricing Strategy" required={true} width={50}>
          <DropdownSelect
            name="strategy_pricing_model"
            label={pricingModel.label ? pricingModel.label : 'Select Strategy'}
            onSelect={setPricingModel}
            onChange={handleChange}
            styleType="bordered"
            formElement={true}
            items={
              merchantPricingModel === 'CPC'
                ? pricingModelItems.filter(item => item.value === 'CPC')
                : pricingModelItems
            }
          />
          <span className="is">is</span>
          <NumberInput
            name="strategy_pricing_model_value"
            value={values.strategy_pricing_model_value}
            onChange={handleChange}
            errors={errors.strategy_pricing_model_value}
          />
          <div className="strategy-payment-model-toggle">
            <Slider onClick={onToggle} selected={pubPaymentModelToggle} />
            <div className="label">Set different publisher payment model</div>
          </div>
        </InputWrapper>
        {pubPaymentModelToggle && (
          <InputWrapper label="Payment Model" width={50}>
            <DropdownSelect
              name="strategy_payment_model"
              label={paymentModel.label ? paymentModel.label : 'Select Model'}
              onSelect={setPaymentModel}
              onChange={handleChange}
              styleType="bordered"
              formElement={true}
              items={[
                { label: 'ROI', value: 'EPP' },
                { label: 'CPC', value: 'EPC' },
                { label: 'COGS', value: 'COGS' },
              ]}
            />
            <span className="is">is</span>
            <NumberInput
              name="strategy_payment_model_value"
              value={values.strategy_payment_model_value}
              onChange={handleChange}
              errors={errors.strategy_payment_model_value}
            />
          </InputWrapper>
        )}
        <InputWrapper label="UTM">
          <TextInput
            name="strategy_utm_query_string"
            value={values.strategy_utm_query_string || ''}
            onChange={handleChange}
            errors={errors.strategy_utm_query_string}
          />
        </InputWrapper>
      </FormSection>
      <FormSection>
        <div className="form-buttons">
          <div className="left">
            {!strategy.strategy_is_evergreen && (
              <Button
                onClick={e => {
                  e.preventDefault();
                  handleBackButton();
                }}
                label="Back"
                kind="text"
                size="large"
              />
            )}
          </div>
          <div className="right">
            <span className="form-error">{formError}</span>
            <Button
              onClick={e => {
                e.preventDefault();
                history.push(
                  `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
                );
              }}
              label="Cancel"
              kind="text"
              size="large"
            />
            <Button submit={true} label={isDirty ? 'Save and Continue' : 'Continue'} size="large" />
          </div>
        </div>
      </FormSection>
    </form>
  );
};

export default SetUpPage;
